/**
 * Blog članci — jedini izvor sadržaja.
 *
 * Backend za blog još ne postoji (`api/Blog/*`), pa članci žive ovde, kao i
 * SEO rečnik. Običan ESM `.js` da bi ga uvezli i aplikacija (`blogStore`) i
 * build-time prerender (`scripts/prerender.mjs`): tako crawler u sirovom HTML-u
 * dobija pun tekst svakog članka, a ne prazan `<div id="root">`.
 *
 * Članci sa tagom "za vlasnike" ciljaju vlasnike salona (partnere); ostali su
 * za klijente. Interni linkovi u sadržaju su obični `<a href>` da ih crawler
 * prati. Tipovi su u `blogPosts.d.ts`.
 */

/** Tag koji označava članke za vlasnike salona. */
export const OWNER_TAG = "za vlasnike";

/** SEO za `/blog` — deli ga aplikacija (`seo.sr.ts`) i prerender. */
export const BLOG_SEO = {
  title: "Blog – saveti za salone i klijente | Rasporedi Me",
  description:
    "Saveti za vlasnike salona (otkazivanje termina, cene, promocije, online zakazivanje) i za klijente (frizura, manikir, nega, zdravlje).",
  keywords:
    "blog za salone, kako smanjiti otkazivanje termina, online zakazivanje saveti, saveti za frizere, saveti za kozmetičare, trendovi frizura, nega noktiju",
  h1: "Blog",
  intro:
    "Saveti za vlasnike salona i korisne informacije iz sveta lepote i zdravlja.",
};

export const BLOG_POSTS = [
  {
    id: 1,
    title: "Kako izabrati pravog frizera za vas",
    slug: "kako-izabrati-pravog-frizera",
    excerpt:
      "Pronalaženje idealnog frizera može biti izazov. U ovom članku delimo savete koji će vam pomoći da napravite pravi izbor — od provere recenzija do prvog termina.",
    content: `
      <p>Vaša frizura je jedan od prvih utisaka koji ostavljate. Dobar frizer ne samo da razume vašu kosu, već i vaš stil i način života. Zato se isplati da odvojite malo vremena i pronađete nekoga ko zaista odgovara vašim potrebama, umesto da birate prvog koji ima slobodan termin.</p>

      <h2>1. Proverite recenzije i ocene</h2>
      <p>Pre nego što zakažete, pogledajte šta drugi klijenti kažu. Na Rasporedi Me možete videti prosečnu ocenu i komentare za svaki salon. Obratite pažnju na komentare koji opisuju sličan tip kose ili frizuru koju vi želite, a ne samo na ukupnu ocenu.</p>

      <h2>2. Pogledajte primere rada</h2>
      <p>Mnogi frizeri dele svoje radove na Instagramu i Facebooku. Na profilu salona potražite linkove ka društvenim mrežama i pogledajte kako izgledaju frizure slične onoj koju tražite — boja, dužina, tip kose.</p>

      <h2>3. Uporedite usluge i cene</h2>
      <p>Cena šišanja često zavisi od dužine i gustine kose, a farbanje od toga koliko je posla. Na profilu salona pogledajte cenovnik i trajanje usluga, pa se unapred spremite za iznos i vreme koje treba da odvojite. Pregledajte <a href="/tenants/hair">frizerske salone</a> ili izaberite grad: <a href="/city/pirot/hair">frizer Pirot</a>, <a href="/city/nis/hair">frizer Niš</a>.</p>

      <h2>4. Zakažite prvu posetu za jednostavniju uslugu</h2>
      <p>Ne morate odmah na veliku promenu. Zakažite trimovanje ili feniranje — to je odlična prilika da upoznate frizera, vidite kako radi i kako komunicira.</p>

      <h2>5. Dobra komunikacija je ključna</h2>
      <p>Dobar frizer će vas saslušati, pogledati vašu kosu i iskreno reći šta je izvodljivo. Ako vam odmah predloži rešenja prilagođena vašoj kosi, umesto da samo klimne glavom, to je dobar znak. Donesite fotografiju frizure koju želite — to uštedi mnogo nesporazuma.</p>

      <h2>6. Lokacija i dostupnost</h2>
      <p>Praktičnost je bitna ako želite da se vraćate redovno. Izaberite salon koji vam je blizu doma ili posla, a termin birajte u vreme koje vam stvarno odgovara. Online zakazivanje vam omogućava da vidite slobodne termine bez pozivanja.</p>

      <blockquote>Savršen frizer nije onaj koji je najskuplji — već onaj koji vas razume.</blockquote>

      <h2>Često postavljana pitanja</h2>
      <h3>Koliko često treba da idem kod frizera?</h3>
      <p>Zavisi od frizure i kose. Kratke frizure traže češće održavanje, a duga kosa može da čeka duže između šišanja. Frizer će vam predložiti raspored posle prve posete.</p>
      <h3>Da li da menjam frizera ako nisam zadovoljan/na?</h3>
      <p>Pre toga pokušajte da kažete šta vam ne odgovara. Ako se ništa ne promeni, potražite novog frizera na osnovu recenzija i primera radova.</p>

      <p>Spremni ste da pronađete svog frizera? <a href="/categories">Pregledajte sve usluge</a> i zakažite termin online za nekoliko minuta.</p>
    `,
    coverImage: "blog/kako-izabrati-pravog-frizera",
    // isecanje gornjih ~8%: u kadru je deo tuđeg brenda
    coverPre: "c_crop,g_south,h_0.92,w_1.0",
    authorName: "Rasporedi Me",
    categoryKey: "hair",
    tags: ["frizeri", "saveti", "kosa"],
    publishedAt: "2026-03-25T10:00:00+01:00",
    creationTime: "2026-03-25T10:00:00+01:00",
  },
  {
    id: 2,
    title: "5 trendova u manikiru za 2026. godinu",
    slug: "trendovi-manikir-2026",
    excerpt:
      "Od minimalističkih dizajna do bold boja — otkrijte koji su trendovi u manikiru dominantni ove godine i kako ih možete isprobati u salonu blizu vas.",
    content: `
      <p>Ove godine je sve o izrazu ličnosti kroz nokte. Bez obzira da li preferirate diskretan ili upečatljiv stil, ovde ćete pronaći inspiraciju, kao i savete kako da trend prilagodite sebi.</p>

      <h2>1. Glazed donut nokti</h2>
      <p>Svetlucavi, biserasti završetak koji izgleda elegantno na svakoj dužini noktiju. Savršen za one koji vole sofisticiran, ali nenametljiv izgled. Dobro stoji uz nežne, mlečne nijanse.</p>

      <h2>2. Bold jednobojni nokti</h2>
      <p>Crvena, kobalt plava, smaragdno zelena — jake, zasićene boje su ponovo in. Jednostavno, a upečatljivo. Jedan ravnomeran sloj gel laka u izazovnoj nijansi i manikir je gotov.</p>

      <h2>3. Micro French</h2>
      <p>Klasični francuski manikir dobija moderan obrt: tanja linija, pastelne boje umesto čisto bele i ponekad zaobljen oblik. Idealan za svakodnevicu i posao.</p>

      <h2>4. 3D ukrasi</h2>
      <p>Za one koji vole da se istaknu — mali cvetovi, biseri i geometrijski oblici. Ovaj trend traži iskusnog majstora, pa obratite pažnju na recenzije i primere radova.</p>

      <h2>5. Negative space dizajn</h2>
      <p>Kombinacija lakiranog i nelakiranog dela nokta stvara moderan, umetnički efekat. Minimalistički je i odlično se slaže sa kratkim noktima.</p>

      <h2>Kako da izaberete trend koji vama odgovara</h2>
      <ul>
        <li><strong>Dužina noktiju:</strong> kratki nokti dobro nose negative space i micro French, dugački 3D ukrase.</li>
        <li><strong>Posao i način života:</strong> ako radite rukama, jednostavniji dizajn će duže izdržati.</li>
        <li><strong>Povod:</strong> za događaje možete biti hrabriji, a za svaki dan birajte nežnije tonove.</li>
        <li><strong>Fotografija:</strong> donesite inspiraciju majstoru, pa će on znati šta je izvodljivo na vašim noktima.</li>
      </ul>

      <h2>Koliko traje i koliko često ponavljati manikir?</h2>
      <p>Trajanje zavisi od vrste manikira i rasta noktiju, a majstor će vam na prvom terminu reći kada je vreme za korekciju. Ako ne znate koju uslugu da izaberete, pitajte — cene i trajanje usluga vidite na profilu salona.</p>

      <p>Želite da isprobate neki od trendova? <a href="/tenants/nails">Pronađite salone za manikir</a> u svom gradu — <a href="/city/pirot/nails">manikir Pirot</a>, <a href="/city/nis/nails">manikir Niš</a> — i zakažite termin online.</p>
    `,
    coverImage: "blog/trendovi-manikir-2026",
    authorName: "Rasporedi Me",
    categoryKey: "nails",
    tags: ["nokti", "trendovi", "manikir"],
    publishedAt: "2026-03-20T14:00:00+01:00",
    creationTime: "2026-03-20T14:00:00+01:00",
  },
  {
    id: 3,
    title: "Zašto je redovan odlazak kod stomatologa važan",
    slug: "redovan-odlazak-kod-stomatologa",
    excerpt:
      "Mnogi od nas odlažu posetu stomatologu dok ne osete bol. Saznajte zašto su redovni pregledi ključni za zdravlje zuba i kako da zakažete termin bez stresa.",
    content: `
      <p>Većina ljudi stomatologu ide tek kada zub zaboli. Problem je što mnoge stomatološke tegobe dugo nemaju simptome, pa kad se pojavi bol, lečenje je obično duže i skuplje. Redovni pregledi su najjednostavniji način da to izbegnete.</p>

      <h2>Prevencija je bolja od lečenja</h2>
      <p>Redovni pregledi — kod većine ljudi na otprilike 6 meseci, ili prema savetu vašeg stomatologa — mogu sprečiti ozbiljne probleme pre nego što nastanu. Rano otkrivanje karijesa, upale desni ili drugih problema znači jednostavnije lečenje.</p>

      <h2>Šta se dešava na pregledu</h2>
      <ul>
        <li><strong>Pregled zuba i desni</strong> — stomatolog proverava stanje zuba, desni i ispuna.</li>
        <li><strong>Razgovor</strong> — pitaće vas o tegobama, navikama i održavanju higijene.</li>
        <li><strong>Plan</strong> — ako ima potrebe za lečenjem, dobijate predlog i okvirni plan.</li>
      </ul>

      <h2>Profesionalno čišćenje zuba</h2>
      <p>Koliko god redovno prali zube, zubni kamenac se vremenom nakuplja. Profesionalno čišćenje uklanja naslage koje četkica ne može, čime se smanjuje rizik od upale desni i neprijatnog zadaha.</p>

      <h2>Rano otkrivanje problema</h2>
      <p>Stomatolog može da uoči rane znake problema koje vi ne možete da vidite — od skrivenih karijesa do promena na sluzokoži. Rani tretman je skoro uvek jednostavniji.</p>

      <h2>Saveti za prevazilaženje straha</h2>
      <ul>
        <li>Izaberite stomatologa sa dobrim recenzijama — poverenje je ključno.</li>
        <li>Zakažite jutarnji termin kada ste odmorniji.</li>
        <li>Recite stomatologu da se plašite — dobar stručnjak će prilagoditi pristup.</li>
        <li>Počnite sa pregledom, ne sa intervencijom.</li>
      </ul>

      <h2>Često postavljana pitanja</h2>
      <h3>Da li je pregled bolan?</h3>
      <p>Običan pregled i čišćenje obično nisu bolni. Ako ste osetljivi, recite to na početku.</p>
      <h3>Kako da izaberem stomatologa?</h3>
      <p>Pogledajte ocene i komentare pacijenata, spisak usluga i cene. Prva poseta za običan pregled je dobar način da procenite da li vam odgovara.</p>

      <p><em>Tekst je opšteg karaktera i ne zamenjuje savet stomatologa.</em></p>

      <p>Pronađite <a href="/tenants/stomatology">stomatološke ordinacije</a> u svom gradu — <a href="/city/pirot/stomatology">stomatolog Pirot</a>, <a href="/city/nis/stomatology">stomatolog Niš</a> — pročitajte recenzije i zakažite termin online.</p>
    `,
    coverImage: "blog/redovan-odlazak-kod-stomatologa",
    authorName: "Rasporedi Me",
    categoryKey: "stomatology",
    tags: ["stomatologija", "zdravlje", "prevencija"],
    publishedAt: "2026-03-15T09:00:00+01:00",
    creationTime: "2026-03-15T09:00:00+01:00",
  },
  {
    id: 4,
    title: "Kako pripremiti kožu pre tretmana šminke",
    slug: "priprema-koze-pre-sminke",
    excerpt:
      "Profesionalna šminka izgleda najbolje na pripremljenom licu. Evo koraka koje možete preduzeti pre odlaska u salon za besprekoran rezultat.",
    content: `
      <p>Čak i najbolji makeup artist ne može da napravi savršen rezultat na nepripremljenoj koži. Nekoliko jednostavnih koraka dan pre i ujutru pre tretmana pravi veliku razliku — šminka se lepše slaže, duže traje i izgleda prirodnije.</p>

      <h2>Nekoliko dana pre</h2>
      <ul>
        <li><strong>Ne eksperimentišite</strong> — izbegavajte nove proizvode, jake piling tretmane i nove kozmetičke postupke pred važan događaj.</li>
        <li><strong>Redovna rutina</strong> — čišćenje, hidratacija i zaštita od sunca svaki dan su najbolja priprema.</li>
        <li><strong>Obrve i depilacija</strong> — ako planirate da ih radite, uradite to bar dan-dva ranije da se koža smiri.</li>
      </ul>

      <h2>Dan pre tretmana</h2>
      <ul>
        <li><strong>Hidracija</strong> — pijte dovoljno vode i uveče nanesite dobru hidratantnu kremu.</li>
        <li><strong>Nežan piling</strong> — blago uklanja mrtve ćelije i stvara glatku podlogu, ali ga nemojte raditi prejako.</li>
        <li><strong>Dovoljno sna</strong> — izgleda trivijalno, ali se vidi na koži.</li>
      </ul>

      <h2>Ujutru pre tretmana</h2>
      <ul>
        <li>Operite lice blagim sredstvom za čišćenje.</li>
        <li>Nanesite laganu hidratantnu kremu i sačekajte da se upije.</li>
        <li>Stavite SPF ako idete napolje pre tretmana.</li>
        <li>Nemojte nanositi prajmer ili puder — to će uraditi makeup artist.</li>
      </ul>

      <h2>Šta poneti sa sobom</h2>
      <p>Ako imate inspiraciju (slike sa Pinteresta ili Instagrama), pokažite je makeup artistu. Dobro je poneti i fotografiju odeće ili frizure, jer šminka treba da se uklopi u celinu.</p>

      <h2>Često postavljana pitanja</h2>
      <h3>Koliko ranije da dođem?</h3>
      <p>Za posebne prilike računajte dovoljno vremena unapred i preciznije se dogovorite sa makeup artistom. Trajanje usluge vidite na profilu salona.</p>
      <h3>Šta ako imam osetljivu kožu?</h3>
      <p>Obavezno recite makeup artistu pre početka, da izabere odgovarajuće proizvode. Za probu koja se ne radi na dan događaja zakažite poseban termin.</p>

      <p>Pronađite iskusne makeup artiste: <a href="/tenants/makeup">šminkanje</a> u vašem gradu, na primer <a href="/city/pirot/makeup">šminker Pirot</a> ili <a href="/city/nis/makeup">šminker Niš</a>, i zakažite termin online.</p>
    `,
    coverImage: "blog/priprema-koze-pre-sminke",
    authorName: "Rasporedi Me",
    categoryKey: "makeup",
    tags: ["šminka", "koža", "priprema"],
    publishedAt: "2026-03-10T11:00:00+01:00",
    creationTime: "2026-03-10T11:00:00+01:00",
  },
  {
    id: 5,
    title: "Online zakazivanje termina: kako funkcioniše i zašto ga ljudi biraju",
    slug: "online-zakazivanje-buducnost",
    excerpt:
      "Zašto sve više ljudi bira online zakazivanje umesto poziva? Otkrijte prednosti digitalnog zakazivanja i kako Rasporedi Me olakšava ceo proces.",
    content: `
      <p>Svi smo bili u situaciji — zovete salon, niko se ne javlja, pa pokušavate ponovo. Ili je linija zauzeta. Sa online zakazivanjem taj problem jednostavno ne postoji, a ceo postupak traje manje od minuta.</p>

      <h2>Prednosti online zakazivanja</h2>
      <ul>
        <li><strong>Dostupno 24/7</strong> — zakažite termin u ponoć, rano ujutru ili tokom pauze na poslu.</li>
        <li><strong>Vidite sve slobodne termine</strong> — birate tačno vreme koje vam odgovara.</li>
        <li><strong>Poređenje salona</strong> — pregledajte ocene, cene i usluge pre zakazivanja.</li>
        <li><strong>Podsetnici</strong> — registrovani korisnici dobijaju podsetnik pre termina.</li>
        <li><strong>Bez pritiska</strong> — birate u miru, bez žurbe telefonskog razgovora.</li>
      </ul>

      <h2>Kako funkcioniše Rasporedi Me?</h2>
      <ol>
        <li>Izaberite kategoriju usluge (frizer, kozmetičar, nokti...).</li>
        <li>Pronađite salon u vašem gradu.</li>
        <li>Izaberite uslugu, datum i vreme.</li>
        <li>Potvrdite — i to je to.</li>
      </ol>
      <p>Ako želite da vidite više, pročitajte <a href="/kako-funkcionise">kako funkcioniše zakazivanje</a>.</p>

      <h2>Online zakazivanje za vlasnike salona</h2>
      <p>Online zakazivanje nije dobro samo za klijente. Salon koji ima link za zakazivanje ne mora da prekida rad zbog telefona, ima manje zabuna oko termina i lakše se pronalazi. Ako vodite salon, pogledajte <a href="/cene">cene i planove za salone</a> ili pročitajte <a href="/blog/sveska-ili-aplikacija-za-zakazivanje">poređenje sveske i aplikacije za zakazivanje</a>.</p>

      <h2>Često postavljana pitanja</h2>
      <h3>Da li je zakazivanje besplatno za klijente?</h3>
      <p>Da, klijenti zakazuju besplatno. Cenu usluge plaćate salonu.</p>
      <h3>Moram li da napravim nalog?</h3>
      <p>Zakazivanje je jednostavno, a registracijom dobijate pregled svojih termina i podsetnike.</p>
      <h3>Šta ako moram da otkažem?</h3>
      <p>Javite salonu što pre, da bi termin mogao da se ponudi drugom klijentu.</p>

      <p>Probajte sami — <a href="/categories">izaberite uslugu</a> i zakažite svoj prvi termin online.</p>
    `,
    coverImage: "blog/online-zakazivanje-buducnost",
    authorName: "Rasporedi Me",
    categoryKey: null,
    tags: ["zakazivanje", "online", "platforma"],
    publishedAt: "2026-03-05T08:00:00+01:00",
    creationTime: "2026-03-05T08:00:00+01:00",
  },
  {
    id: 6,
    title: "Vodič za izbor masaže: koja je prava za vas?",
    slug: "vodic-za-izbor-masaze",
    excerpt:
      "Relax, sportska, anticelulit ili terapeutska? Svaka masaža ima svoju namenu. Saznajte koja vrsta masaže vam najviše odgovara.",
    content: `
      <p>Masaža je mnogo više od opuštanja. Prava vrsta masaže može da ublaži napetost, poboljša osećaj u mišićima i pomogne u oporavku. Ali kako znati koja je prava za vas?</p>

      <h2>Relax masaža</h2>
      <p>Idealna za oslobađanje svakodnevnog stresa. Koristi lagane, dugačke pokrete koji opuštaju mišiće. Dobar izbor ako vam treba pauza od užurbanog tempa ili ste prvi put na masaži.</p>

      <h2>Sportska masaža</h2>
      <p>Namenjena aktivnim ljudima i sportistima. Fokusira se na mišićne grupe koje su opterećene treningom. Intenzivnija je od relax masaže i često se radi pre ili posle napora.</p>

      <h2>Anticelulit masaža</h2>
      <p>Kombinuje intenzivnije tehnike za poboljšanje cirkulacije i izgleda kože. Najbolje je kao deo redovne rutine, uz zdrave navike.</p>

      <h2>Terapeutska masaža</h2>
      <p>Za tegobe u leđima, vratu ili ramenima. Terapeut se fokusira na problematične zone, a po potrebi i usmerava na lekara. Ako imate dugotrajne ili jake bolove, prvo se posavetujte sa lekarom.</p>

      <h2>Kako da izaberete masažu</h2>
      <ul>
        <li><strong>Cilj:</strong> opuštanje, oporavak posle treninga ili rešavanje tegoba.</li>
        <li><strong>Trajanje:</strong> od kraćih tretmana za leđa do dužih za celo telo.</li>
        <li><strong>Iskustvo:</strong> ako ste početnik, počnite od relax masaže.</li>
        <li><strong>Zdravstveno stanje:</strong> obavezno recite terapeutu za povrede, trudnoću ili hronične bolesti.</li>
      </ul>

      <blockquote>Ako niste sigurni koja masaža vam treba, recite terapeutu šta osećate — preporučiće pravu vrstu.</blockquote>

      <h2>Često postavljana pitanja</h2>
      <h3>Koliko često treba ići na masažu?</h3>
      <p>Zavisi od cilja. Za opuštanje može povremeno, a za tegobe ili oporavak terapeut predlaže dinamiku.</p>
      <h3>Kako da se pripremim?</h3>
      <p>Dođite odmorni, bez teškog obroka neposredno pre, i recite terapeutu sve što je važno o vašem zdravlju.</p>

      <p><em>Tekst je opšteg karaktera i ne zamenjuje savet lekara.</em></p>

      <p>Pronađite <a href="/tenants/massage">masere</a> u svom gradu — <a href="/city/pirot/massage">masaža Pirot</a>, <a href="/city/nis/massage">masaža Niš</a> — i zakažite termin za masažu koja vam odgovara.</p>
    `,
    coverImage: "blog/vodic-za-izbor-masaze",
    authorName: "Rasporedi Me",
    categoryKey: "massage",
    tags: ["masaža", "zdravlje", "opuštanje"],
    publishedAt: "2026-03-01T12:00:00+01:00",
    creationTime: "2026-03-01T12:00:00+01:00",
  },
  {
    id: 101,
    title: "Kako smanjiti broj otkazanih termina u salonu",
    slug: "kako-smanjiti-broj-otkazanih-termina",
    excerpt:
      "Prazna stolica zbog klijenta koji se nije pojavio košta te vremena i novca. Evo nekoliko jednostavnih navika koje smanjuju otkazivanja i no-show situacije.",
    content: `
      <p>Svaki termin koji ostane prazan, a nije otkazan na vreme, znači izgubljen sat rada. Dobra vest je da većina klijenata ne izostaje iz nepoštovanja — jednostavno zaborave. Zato se problem rešava navikama, a ne strogoćom.</p>

      <h2>1. Podsetite klijenta pre termina</h2>
      <p>Najjeftiniji način da smanjite izostanke je podsetnik. Klijent koji dobije obaveštenje pre termina ima priliku da dođe ili da na vreme javi da ne može. U <a href="/cene">programu za zakazivanje RasporediMe</a> klijenti sa nalogom automatski dobijaju push podsetnik, pa ne morate sami da pišete svakome.</p>

      <h2>2. Olakšajte otkazivanje</h2>
      <p>Zvuči suprotno, ali klijent koji lako može da otkaže će to i uraditi — umesto da jednostavno ne dođe. Kada otkazivanje traži poziv koji niko ne podiže, rezultat je prazan termin bez ikakvog upozorenja.</p>

      <h2>3. Jasno napišite pravila</h2>
      <p>Napišite uz link za zakazivanje koliko unapred se termin otkazuje. Većini klijenata je dovoljno da znaju da je to vama važno.</p>

      <h2>4. Oslobođeni termin odmah ponudite dalje</h2>
      <p>Kada klijent otkaže, termin ne mora da propadne. Napravite kratku promociju ili javite stalnim klijentima da je termin slobodan. O tome više u članku <a href="/blog/kako-popuniti-prazne-termine">kako popuniti prazne termine</a>.</p>

      <h2>5. Neka klijent sam izabere termin</h2>
      <p>Klijenti koji sami biraju termin kad im odgovara ređe izostaju od onih kojima je termin „dogovoren u prolazu“. Online zakazivanje daje klijentu osećaj odgovornosti za sopstveni izbor.</p>

      <p>Želite da podsetnici rade umesto vas? Pogledajte <a href="/cene">cene</a> ili nam se <a href="/contact">javite</a> — prvih 14 dana je besplatno.</p>
    `,
    coverImage: "blog/kako-smanjiti-broj-otkazanih-termina",
    authorName: "Rasporedi Me",
    categoryKey: null,
    tags: ["za vlasnike", "otkazivanje", "podsetnici"],
    publishedAt: "2026-04-10T10:00:00+02:00",
    creationTime: "2026-04-10T10:00:00+02:00",
  },
  {
    id: 102,
    title: "Sveska ili aplikacija za zakazivanje: šta se više isplati",
    slug: "sveska-ili-aplikacija-za-zakazivanje",
    excerpt:
      "Sveska je besplatna i svima poznata, ali ima svoju cenu. Poredimo svesku i aplikaciju za zakazivanje kroz situacije iz svakodnevnog rada salona.",
    content: `
      <p>Većina salona počinje sa sveskom i olovkom, i to je sasvim u redu. Pitanje je u kom trenutku sveska počne da košta više nego što štedi.</p>

      <h2>Gde sveska radi dobro</h2>
      <p>Za jednog radnika sa malo klijenata, sveska je jednostavna, ne traži internet i ne traži učenje. Ako vam se ne dešava da gubite termine, nema razloga da žurite.</p>

      <h2>Gde sveska počinje da smeta</h2>
      <ul>
        <li><strong>Telefon dok radite.</strong> Svaki poziv usred usluge prekida koncentraciju i vas i klijenta.</li>
        <li><strong>Dupli termini i pogrešno pročitani datumi.</strong> Greška u svesci se otkrije tek kada klijent već stoji na vratima.</li>
        <li><strong>Više radnika.</strong> Sveska ne pokazuje ko je slobodan bez dodatnog prepisivanja.</li>
        <li><strong>Odmor.</strong> Dok ste na odmoru, klijenti i dalje zovu, a vi ih morate otkazivati ili preusmeravati.</li>
        <li><strong>Pregled posla.</strong> Koliko ste zaradili ovog meseca i koje usluge se najviše traže, u svesci morate da računate ručno.</li>
      </ul>

      <h2>Šta menja aplikacija</h2>
      <p>Klijenti sami biraju slobodan termin preko linka, bilo kad. Termin se upisuje na jedno mesto, nema duplih termina, a odmor se blokira jednim klikom. Klijenti sa nalogom dobijaju podsetnik pre termina, a u Salon planu dobijate i pregled zarade po radniku bez računanja.</p>

      <h2>Pošteno poređenje troška</h2>
      <p>Aplikacija ima mesečnu cenu — kod nas je to <a href="/cene">1.499 RSD za Solo plan</a>. Procenite koliko košta jedan izgubljen ili zaboravljen termin u vašem salonu, pa uporedite.</p>

      <p>Možete da probate bez rizika: <a href="/contact">14 dana je besplatno</a>, bez kreditne kartice.</p>
    `,
    coverImage: "blog/sveska-ili-aplikacija-za-zakazivanje",
    authorName: "Rasporedi Me",
    categoryKey: null,
    tags: ["za vlasnike", "poređenje", "sveska"],
    publishedAt: "2026-04-14T10:00:00+02:00",
    creationTime: "2026-04-14T10:00:00+02:00",
  },
  {
    id: 103,
    title: "Kako da klijenti zakazuju termine preko Instagrama",
    slug: "zakazivanje-termina-preko-instagrama",
    excerpt:
      "Klijenti te nalaze na Instagramu, a zakazuju porukama koje ostanu bez odgovora. Evo kako da link za zakazivanje postane deo tvog profila.",
    content: `
      <p>Za mnoge salone i samostalne majstore Instagram je glavna izložba rada. Problem je što posle divljenja fotografiji klijent mora da piše poruku i čeka odgovor, a to je upravo trenutak kada većina odustane.</p>

      <h2>1. Stavite link za zakazivanje u bio</h2>
      <p>Jedan klik od fotografije do slobodnih termina. Klijent ne mora da vas pita „kada imate slobodno“ — vidi sam. U RasporediMe dobijate sopstveni link koji možete da stavite u bio, story ili poruku.</p>

      <h2>2. U poruci ponudite link, ne dopisivanje</h2>
      <p>Kada neko ipak napiše u inbox, odgovorite jednom rečenicom i linkom. Ušteda vremena je velika, a klijent dobija odgovor odmah, čak i kada niste za telefonom.</p>

      <h2>3. Podelite link u storiju kada se oslobode termini</h2>
      <p>Ako se oslobodi termin ili imate akciju, podelite link u storiju. Klijenti koji vas već prate sada imaju direktan put do rezervacije.</p>

      <h2>4. Pokažite uslugu, pa cenu</h2>
      <p>Što više informacija klijent dobije pre zakazivanja (usluga, trajanje, cena), to je manje pitanja u porukama. U aplikaciji sve to već stoji uz uslugu.</p>

      <h2>5. Koristite isti link svuda</h2>
      <p>Instagram, Google profil, vizit karta, poruke. Tako klijenti uvek završe na istom mestu.</p>

      <p>Ako radite sami ili imate mali tim, <a href="/za-nail-artiste">nail artisti</a>, <a href="/za-makeup-artiste">makeup artisti</a> i <a href="/za-frizere">frizeri</a> već koriste ovakav link. <a href="/contact">Pridružite se i probajte 14 dana besplatno.</a></p>
    `,
    coverImage: "blog/zakazivanje-termina-preko-instagrama",
    authorName: "Rasporedi Me",
    categoryKey: null,
    tags: ["za vlasnike", "instagram", "online zakazivanje"],
    publishedAt: "2026-04-18T10:00:00+02:00",
    creationTime: "2026-04-18T10:00:00+02:00",
  },
  {
    id: 104,
    title: "Kako odrediti cene usluga u salonu",
    slug: "kako-odrediti-cene-usluga-u-salonu",
    excerpt:
      "Preniske cene te iscrpljuju, previsoke teraju klijente. Pet koraka da izračunaš cenu koja pokriva trošak, vreme i tvoj rad.",
    content: `
      <p>Cena usluge nije samo broj koji klijent plaća. To je odgovor na pitanje koliko vredi vaše vreme, materijal i iskustvo. Ako cene određujete „po osećaju“ ili ih prepisujete od konkurencije, verovatno ostavljate novac na stolu.</p>

      <h2>1. Izračunajte koliko zaista traje usluga</h2>
      <p>Računajte sve: pripremu, uslugu, čišćenje i vreme između klijenata. Usluga koja traje 45 minuta u praksi često zauzme sat vremena.</p>

      <h2>2. Saberite fiksne troškove</h2>
      <p>Zakup, struja, voda, osiguranje, porezi, prevoz. Podelite mesečni iznos sa brojem radnih sati i dobićete trošak jednog sata rada salona.</p>

      <h2>3. Dodajte trošak materijala</h2>
      <p>Boja, lak, rukavice, šamponi, jednokratni materijal. Zapišite koliko stvarno potrošite po usluzi.</p>

      <h2>4. Odredite koliko želite da zaradite</h2>
      <p>Cena mora da pokrije troškove i da ostavi vama platu. Ako posle svih troškova ostane premalo, cena je preniska, bez obzira na to šta naplaćuje konkurencija.</p>

      <h2>5. Razdvojite cene po dužini i složenosti</h2>
      <p>Šišanje kratke i duge kose ne traje isto, pa ne treba ni da košta isto. Jasno izdvojene cene i trajanja daju klijentu poverenje, a vama tačan raspored. U <a href="/cene">aplikaciji za salone RasporediMe</a> svaka usluga ima svoje trajanje i cenu, a kalendar sam blokira potrebno vreme.</p>

      <p>U Salon planu pregled prometa po radniku pokazuje koliko donosi svaki član tima. <a href="/contact">Probajte 14 dana besplatno.</a></p>
    `,
    coverImage: "blog/kako-odrediti-cene-usluga-u-salonu",
    authorName: "Rasporedi Me",
    categoryKey: null,
    tags: ["za vlasnike", "cene", "poslovanje"],
    publishedAt: "2026-04-22T10:00:00+02:00",
    creationTime: "2026-04-22T10:00:00+02:00",
  },
  {
    id: 105,
    title: "Kako popuniti prazne termine u salonu",
    slug: "kako-popuniti-prazne-termine",
    excerpt:
      "Svaki salon ima slabije sate i dane. Umesto da čekate da se popune sami, evo pet načina da ih popunite promocijama i boljom organizacijom.",
    content: `
      <p>Prazni termini nisu uvek znak da posao ide loše. Obično su to isti sati iz nedelje u nedelju — rano ujutru, sredina dana ili početak nedelje. A ono što se ponavlja, može da se planira.</p>

      <h2>1. Pronađite svoje slabe sate</h2>
      <p>Pogledajte poslednjih mesec dana i označite kada najčešće ostajete bez klijenata. Tek kad znate gde su rupe, možete da ih popunjavate.</p>

      <h2>2. Ponudite popust baš za te sate</h2>
      <p>Popust na usluge koje se slabije traže, u periodu kad vam je mirnije, neće umanjiti vašu zaradu u najjačim danima, ali će privući nove klijente. Promocije i kuponi u <a href="/cene">Salon planu RasporediMe</a> omogućavaju da popust važi za izabrane usluge u izabranom periodu.</p>

      <h2>3. Obavestite klijente jednim klikom</h2>
      <p>Akcija bez obaveštenja nikome ne koristi. Kada napravite promociju, korisnici aplikacije dobijaju obaveštenje, pa ne morate da šaljete poruke pojedinačno.</p>

      <h2>4. Spojite usluge u paket</h2>
      <p>Šišanje i farbanje, manikir i pedikir, tretman lica i masaža. Paket povećava vrednost jedne posete i lakše popunjava duže termine.</p>

      <h2>5. Ponudite oslobođene termine stalnim klijentima</h2>
      <p>Kad neko otkaže, javite odmah — podelite link u storiju ili pošaljite promociju. Više o tome u članku <a href="/blog/kako-smanjiti-broj-otkazanih-termina">kako smanjiti broj otkazanih termina</a>.</p>

      <p>Želite alat koji ovo radi umesto vas? <a href="/contact">Javite nam se</a> — prvih 14 dana je besplatno.</p>
    `,
    coverImage: "blog/kako-popuniti-prazne-termine",
    authorName: "Rasporedi Me",
    categoryKey: null,
    tags: ["za vlasnike", "promocije", "kuponi"],
    publishedAt: "2026-04-26T10:00:00+02:00",
    creationTime: "2026-04-26T10:00:00+02:00",
  },
  {
    id: 106,
    title: "Šta treba da ima dobra aplikacija za vođenje salona",
    slug: "sta-treba-da-ima-aplikacija-za-salon",
    excerpt:
      "Pre nego što izaberete aplikaciju za zakazivanje, proverite ovih sedam stvari. Pomoći će vam da izbegnete softver koji vas samo komplikuje.",
    content: `
      <p>Tržište aplikacija za zakazivanje je veliko, a većina ih izgleda slično. Razlika se vidi tek kada krenete da ih koristite svaki dan. Evo na šta vredi da obratite pažnju pre nego što se odlučite.</p>

      <h2>1. Klijenti zakazuju sami</h2>
      <p>Što je manje koraka do rezervacije, to je više rezervacija. Link za zakazivanje treba da radi na telefonu i bez komplikovanog postupka.</p>

      <h2>2. Više radnika i izbor omiljenog</h2>
      <p>Ako imate tim, svaki radnik treba da ima sopstveni raspored, a klijenti da mogu da biraju kod koga žele.</p>

      <h2>3. Automatski podsetnici</h2>
      <p>Najbolja zaštita od izostanaka. Bez podsetnika ćete ih slati ručno ili trpeti prazne termine. Proverite da podsetnici stižu klijentima automatski.</p>

      <h2>4. Pregled prometa i zarade</h2>
      <p>Treba da znate koliko ste zaradili, koje usluge nose posao i koji radnik koliko radi — bez ručnog računanja.</p>

      <h2>5. Kuponi i promocije</h2>
      <p>Alat koji popunjava prazne termine vredi više od lepog kalendara.</p>

      <h2>6. Srpski jezik, podrška i cene u dinarima</h2>
      <p>Interfejs i podrška na srpskom uštedeće vam mnogo nerviranja. Proverite i da su cene jasno navedene u RSD, bez ugovorne obaveze.</p>

      <h2>7. Besplatan probni period</h2>
      <p>Aplikaciju možete oceniti samo kad je koristite. Tražite probu bez kreditne kartice. <a href="/cene">RasporediMe cene i planovi</a> uključuju 14 dana besplatnog probnog perioda.</p>

      <p>Pogledajte <a href="/kako-funkcionise">kako funkcioniše</a> ili <a href="/contact">pošaljite upit</a> pa ćemo vas provesti kroz sve.</p>
    `,
    coverImage: "blog/sta-treba-da-ima-aplikacija-za-salon",
    authorName: "Rasporedi Me",
    categoryKey: null,
    tags: ["za vlasnike", "izbor aplikacije", "softver za salon"],
    publishedAt: "2026-04-30T10:00:00+02:00",
    creationTime: "2026-04-30T10:00:00+02:00",
  },
];

/* ── Naslovne slike (Cloudinary, folder `blog/`) ─────────────────── */

/** Stranica članka: 16:9, do 1200px. */
export const BLOG_COVER_TRANSFORM = "f_auto,q_auto,w_1200,ar_16:9,c_fill";
/** Kartica na `/blog`. */
export const BLOG_CARD_TRANSFORM = "f_auto,q_auto,w_700,ar_16:9,c_fill";
/** og:image i JSON-LD: JPG, jer neki crawleri ne podržavaju AVIF/WebP. */
export const BLOG_OG_TRANSFORM = "f_jpg,q_auto,w_1200,h_630,c_fill";

/**
 * URL naslovne slike. `coverImage` je Cloudinary public ID (npr.
 * `blog/kako-izabrati-pravog-frizera`) ili pun URL; prazno znači bez slike.
 * `coverPre` je opcioni transformacioni korak pre kadriranja (npr. isecanje
 * dela slike koji ne želimo da se vidi).
 */
export const blogCoverUrl = (baseUrl, post, transform = BLOG_COVER_TRANSFORM) => {
  const id = post.coverImage;
  if (!id) return "";
  if (/^https?:\/\//.test(id)) return id;
  if (!baseUrl) return "";
  const steps = [post.coverPre, transform].filter(Boolean).join("/");
  return `${baseUrl}/image/upload/${steps}/${id}`;
};
